import React, { useEffect, useRef, useState } from "react";
import { useMutation } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import WorkflowStartRequestModel from "../../Interfaces/Requests/Administration/WorkflowStartRequestModel"
import WorkflowHistoryRequestModel from "../../Interfaces/Requests/Administration/WorkflowHistoryRequestModel";
import {
    Alert as ServaAlert,
    Button,
    Col,
    DeleteIcon,
    Divider,
    Modal as ServaModal,
    Row,
    Spin,
} from '@servahealth/serva-ui-kit';

export interface MultiStepEvent {
    sequence: number;
    stepName: string;
    status: string;
    message: string;
    percent?: number;
    timestampUtc?: string;
    errorCode?: string | null;
    errorDetails?: string | null;
    [key: string]: any;
}

export interface StartJobResponse {
    jobId: string;
    status?: string;
}

export interface MultiStepJobViewerProps {
    title?: string;
    startConfirmationTitle?: string;
    startConfirmationMessage?: React.ReactNode;
    startButtonText?: string;
    startButtonDisabled?: boolean;
    stepsTitle?: string;

    startUrl: string;
    buildStartBody: () => WorkflowStartRequestModel;

    historyUrlBuilder: (jobId: string) => string;
    eventUrlBuilder: (jobId: string) => string;

    eventName?: string;
    reconnectDelayMs?: number;
    withCredentials?: boolean;

    onWorkflowEnded?: (jobId: string, terminalStatus: string) => void;

    renderStep?: (step: MultiStepEvent, index: number) => React.ReactNode;
}

const MultiStepJobViewer: React.FC<MultiStepJobViewerProps> = function (props) {
    const {
        title = "Job Progress",
        startConfirmationMessage,
        startConfirmationTitle = "Generic Confirm Workflow Start",
        startButtonText = "Start",
        startButtonDisabled = false,
        stepsTitle = "Steps",
        startUrl,
        buildStartBody,
        historyUrlBuilder,
        eventUrlBuilder,
        eventName = "progress",
        reconnectDelayMs = 1000,
        withCredentials = true,
        onWorkflowEnded,
        renderStep
    } = props;

    const [jobId, setJobId] = useState<string>("");
    const [steps, setSteps] = useState<MultiStepEvent[]>([]);
    const [status, setStatus] = useState<string>("idle");
    const [connectionState, setConnectionState] = useState<string>("disconnected");

    const [startConfirmationOpen, setStartConfirmationOpen] = useState<boolean>(false);

    const isTerminalRef = useRef<boolean>(false);
    const eventSourceRef = useRef<EventSource | null>(null);
    const lastSequenceRef = useRef<number>(0);
    // Prevents orphaned reconnect timers from continuing after completion/unmount/new start.
    const reconnectTimeoutRef = useRef<number | null>(null);
    const stepsEndRef = useRef<HTMLDivElement | null>(null);
    const hasNotifiedWorkflowEndedRef = useRef<boolean>(false);

    const [isWorkflowRunning, setIsWorkflowRunning] = useState<boolean>(false);
    const [stepChangeTick, setStepChangeTick] = useState<number>(0);

    const [currentError, setCurrentError] = useState('');

    // Central cleanup for pending reconnect attempts.
    function clearReconnectTimeout(): void {
        if (reconnectTimeoutRef.current !== null) {
            window.clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }

    function notifyWorkflowEnded(targetJobId: string, terminalStatus: string): void {
        if (hasNotifiedWorkflowEndedRef.current) {
            return;
        }

        hasNotifiedWorkflowEndedRef.current = true;

        if (isTerminalStatus(terminalStatus) && onWorkflowEnded) {
            onWorkflowEnded(targetJobId, terminalStatus);
        }
    }

    // Keeps terminal checks consistent.
    function isTerminalStatus(value: string | undefined | null): boolean {
        return value === "finished" || value === "failed" || value === "canceled";
    }

    function getCurrentStepTitle(items: MultiStepEvent[]): string {
        if (!items || items.length === 0) {
            return stepsTitle;
        }

        return items[items.length - 1]?.stepName || stepsTitle;
    }

    function formatStatus(value: string | undefined | null): string {
        if (!value) {
            return '';
        }

        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    // Safe error formatting for Axios/non-Axios errors.
    function getErrorMessage(error: any): string {
        return error?.response?.data?.message || error?.message || String(error);
    }

    function mergeSteps(existing: MultiStepEvent[], incoming: MultiStepEvent[]): MultiStepEvent[] {
        const map = new Map<number, MultiStepEvent>();

        existing.forEach(function (s) {
            map.set(s.sequence, s);
        });

        incoming.forEach(function (s) {
            map.set(s.sequence, s);
        });

        return Array.from(map.values()).sort(function (a, b) {
            return a.sequence - b.sequence;
        });
    }

    const { mutateAsync: getHistory, isError: isGetHistoryError, error: getHistoryError, reset: resetGetHistory } = useMutation(
        async ({ targetJobId, afterSequence }: { targetJobId: string; afterSequence: number; }) => {
            try {
                const url = historyUrlBuilder(targetJobId);
                const requestData = { afterSequence: afterSequence };

                const responseData = await AxiosUtil.get<MultiStepEvent[], WorkflowHistoryRequestModel>(url, requestData);
                return responseData;
            } catch (error) {
                 // Using safer error message.
                setCurrentError('Workflow History: Internal Error: ' + getErrorMessage(error));

                // return Promise.reject(error); // COMMENTED OUT: throwing is cleaner inside async functions.
                // Preserves mutateAsync rejection while avoiding explicit Promise.reject.
                throw error;
            }
        },
        {
            onSuccess: (history: MultiStepEvent[], variables: { targetJobId: string; afterSequence: number; }) => {
                setSteps(function (prev) {
                    return mergeSteps(prev, history);
                });

                if (history.length > 0) {
                    const sortedHistory = [...history].sort(function (a, b) {
                        return a.sequence - b.sequence;
                    });
                    const last = sortedHistory[sortedHistory.length - 1];

                    lastSequenceRef.current = last.sequence;
                    setStatus(last.status);
                    setStepChangeTick(function (value) {
                        return value + 1;
                    });

                    // Consistent terminal check.
                    if (isTerminalStatus(last.status)) {
                        isTerminalRef.current = true;
                        // Stop pending reconnects after history proves terminal state.
                        clearReconnectTimeout();
                        // Keep connection state aligned when terminal status is discovered from history.
                        setConnectionState("completed");
                        setIsWorkflowRunning(false);

                        notifyWorkflowEnded(variables.targetJobId, last.status);
                    }
                }
            },
            onError: (err: any) => {
                setCurrentError('Workflow History: Internal Error: ' + getErrorMessage(err));
                console.error('Workflow History Error:', err);
            }
        }
    );

    function connectToEvents(targetJobId: string): void {
        // Avoid duplicate reconnect timers.
        clearReconnectTimeout();

        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            // Avoid keeping stale EventSource reference.
            eventSourceRef.current = null;
        }

        // Do not open a new stream after terminal state.
        if (isTerminalRef.current) {
            setConnectionState("completed");
            return;
        }

        setConnectionState("connecting");

        const eventSource = new EventSource(eventUrlBuilder(targetJobId), {
            withCredentials: withCredentials
        });

        eventSourceRef.current = eventSource;

        eventSource.onopen = function () {
            if (!isTerminalRef.current) {
                setConnectionState("connected");
            }
        };

        eventSource.addEventListener(eventName, function (event) {
            // Allows safe JSON parsing.
            let payload: MultiStepEvent;

            // Malformed SSE payload should not crash the component.
            try {
                payload = JSON.parse(event.data) as MultiStepEvent;
            } catch (error) {
                setCurrentError('Workflow Event Parse Error: ' + getErrorMessage(error));
                console.error('Workflow Event Parse Error:', error);
                return;
            }

            setSteps(function (prev) {
                return mergeSteps(prev, [payload]);
            });

            lastSequenceRef.current = payload.sequence;
            setStatus(payload.status);
            setStepChangeTick(function (value) {
                return value + 1;
            });

            // Consistent terminal check.
            if (isTerminalStatus(payload.status)) {
                isTerminalRef.current = true;
                // Prevent delayed reconnect after final event.
                clearReconnectTimeout();
                setConnectionState("completed");
                setIsWorkflowRunning(false);

                notifyWorkflowEnded(targetJobId, payload.status);

                eventSource.close();
                // Clear closed EventSource reference.
                eventSourceRef.current = null;
            }
        });

        eventSource.onerror = async function () {
            eventSource.close();

            // Only clear the active EventSource.
            if (eventSourceRef.current === eventSource) {
                eventSourceRef.current = null;
            }

            if (isTerminalRef.current) {
                // Make terminal branch idempotent.
                clearReconnectTimeout();
                setConnectionState("completed");
                return;
            }

            setConnectionState("reconnecting");

            try {
                await getHistory({
                    targetJobId: targetJobId,
                    afterSequence: lastSequenceRef.current
                });
            } catch (error) {
                // Call-site error handling for mutateAsync rejection.
                setCurrentError('Workflow History: Internal Error: ' + getErrorMessage(error));
                setConnectionState("disconnected");
                return;
            }

            if (isTerminalRef.current) {
                // Stop reconnect if history fetched final status.
                clearReconnectTimeout();
                setConnectionState("completed");
                return;
            }

            // Save timer id so it can be cancelled.
            reconnectTimeoutRef.current = window.setTimeout(
                function () {
                    // Mark timer as consumed.
                    reconnectTimeoutRef.current = null;

                    // Guard against terminal state reached while timer was pending.
                    if (isTerminalRef.current) {
                        setConnectionState("completed");
                        return;
                    }

                    connectToEvents(targetJobId);
                }, reconnectDelayMs
            );
        };
    }

    const { mutateAsync: startWorkflow, isLoading: isStartWorkflowLoading, isError: isStartWorkflowError, error: getStartWorkflowError, reset: resetStartWorkflow } = useMutation(
        async () => {
            try {
                const requestData = buildStartBody();
                const responseData = await AxiosUtil.post<StartJobResponse, WorkflowStartRequestModel>(startUrl, requestData);
                return responseData;
            } catch (error) {
                setCurrentError('Workflow Job Start: Internal Error: ' + getErrorMessage(error));

                // return Promise.reject(error); // COMMENTED OUT: throwing is cleaner inside async functions.
                throw error; // CHANGED: preserves mutateAsync rejection while avoiding explicit Promise.reject.
            }
        },
        {
            onSuccess: async (data: StartJobResponse) => {
                // Defensive check before starting history/SSE flow.
                if (!data || !data.jobId) {
                    setCurrentError('Workflow Job Start: Internal Error: missing jobId from start response.');
                    setStatus("failed");
                    setConnectionState("disconnected");
                    isTerminalRef.current = true;
                    setIsWorkflowRunning(false);
                    return;
                }

                setJobId(data.jobId);
                setStatus(data.status ?? "queued");

                try {
                    await getHistory({targetJobId: data.jobId, afterSequence: 0});
                } catch(error) {
                    setCurrentError('Workflow Job Start: Internal Error: ' + getErrorMessage(error));
                    setStatus("failed");
                    setConnectionState("disconnected");
                    // Avoid reconnecting after start-time history failure.
                    isTerminalRef.current = true;
                    setIsWorkflowRunning(false);
                    // Do not connect to SSE after history startup failure.
                    return;
                }

                // Do not open SSE if initial history already contains terminal status.
                if (isTerminalRef.current) {
                    setConnectionState("completed");
                    return;
                }

                connectToEvents(data.jobId);
            },
            onError: (err: any) => {
                setCurrentError('Workflow Job Start: Internal Error: ' + getErrorMessage(err));
                setStatus("failed");
                setConnectionState("disconnected");
                // Prevent reconnect attempts after start failure.
                isTerminalRef.current = true;
                setIsWorkflowRunning(false);
            }
        }
    );

    async function startWorkflowJob(): Promise<void> {
        // Cancel reconnect from prior run.
        clearReconnectTimeout();

        // Close any prior active stream before starting a new job.
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        setSteps([]);
        setStatus("starting");
        setConnectionState("connecting");
        lastSequenceRef.current = 0;
        isTerminalRef.current = false;
        hasNotifiedWorkflowEndedRef.current = false;

        setIsWorkflowRunning(true);

        resetGetHistory();
        resetStartWorkflow();
        setCurrentError('');

        try {
            await startWorkflow();
        } catch(error) {
            setCurrentError('Workflow Job Start: Internal Error: ' + getErrorMessage(error));
            setStatus("failed");
            setConnectionState("disconnected");
            // Keep terminal state consistent after failed start.
            isTerminalRef.current = true;
        }
    }

    async function handleStartButtonClick(): Promise<void> {
        resetGetHistory();
        resetStartWorkflow();
        setCurrentError('');

        if (startConfirmationMessage) {
            setStartConfirmationOpen(true);
            return;
        }

        await startWorkflowJob();
    }

    async function handleStartConfirmationOk(): Promise<void> {
        setStartConfirmationOpen(false);
        await startWorkflowJob();
    }

    function handleStartConfirmationCancel(): void {
        setStartConfirmationOpen(false);
    }

    useEffect(function () {
        return function () {
            // Avoid reconnect after unmount.
            clearReconnectTimeout();

            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                // Clear closed EventSource reference.
                eventSourceRef.current = null;
            }
        };
    }, []);

    useEffect(function () {
        if (stepsEndRef.current) {
            stepsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [steps]);

    const determineWhichError = () => {
        if ( isGetHistoryError ) {
            return currentError + ' ' + getErrorMessage(getHistoryError);
        }
        else if ( isStartWorkflowError ) {
            return currentError + ' ' + getErrorMessage(getStartWorkflowError);
        }
        else {
            return currentError;
        }
    } 
    
    return (
        <Spin spinning={ isStartWorkflowLoading }>
            <div style={{ margin: '0 auto', paddingLeft: '16px', paddingRight: '16px', textAlign: 'center', width: '75%' }}>
            {
                (
                    isGetHistoryError ||
                    isStartWorkflowError ||
                    currentError !== ''
                )
                &&
                (
                    <ServaAlert
                        className={'mb-2'}
                        message={determineWhichError()}
                        showIcon={true}
                        type="error"
                    ></ServaAlert>
                )
            }
            </div>
            <ServaModal
                open={startConfirmationOpen}
                title={
                    <div style={{textAlign: 'center', fontSize: '20px', fontWeight: 'bold', width: '100%', }}>
                        {startConfirmationTitle}
                    </div>
                }
                onOk={handleStartConfirmationOk}
                onCancel={handleStartConfirmationCancel}
            >
                <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                <div style={{textAlign: 'center', fontSize: '14px', fontWeight: 'bold', width: '100%', }}>
                    {startConfirmationMessage}
                </div>
                <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
            </ServaModal>
            <Row gutter={16} style={{ padding: '16px', paddingBottom: '4px', paddingTop: '4px' }}>
                <Col span={1} />
                <Col span={24}>
                    <h5 className='text-2xl pt-2 px-2'>{title}</h5>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div><strong>Job ID:</strong> {jobId || "(none)"}</div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <strong>Status:</strong> {formatStatus(status)}
                    </div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div><strong>Connection:</strong> {connectionState}</div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                    </div>
                </Col>
                <Col span={1} />
            </Row>

            <Row gutter={16}>
                <Col span={1} />
                <Col span={22}>
                    <div style={{ marginTop: 12 }}>
                        <h5>{getCurrentStepTitle(steps)}</h5>
                        <div
                            className={isWorkflowRunning ? 'workflow-step-frame workflow-step-frame-running' : 'workflow-step-frame'}
                            key={stepChangeTick}
                            style={{
                                alignItems: 'center',
                                animation: isWorkflowRunning ? undefined : 'workflow-step-washover 0.9s ease-out',
                                border: '1px solid black',
                                display: 'flex',
                                minHeight: '120px',
                                padding: '8px',
                            }}
                        >
                            {
                                steps.length > 0
                                ?
                                (
                                    renderStep
                                    ?
                                    renderStep(steps[steps.length - 1], steps.length - 1)
                                    :
                                    <div>
                                        <div>
                                            <strong>{steps[steps.length - 1].stepName}</strong> - {steps[steps.length - 1].status} - {steps[steps.length - 1].message} ({steps[steps.length - 1].percent ?? 0}%)
                                        </div>
                                        {
                                            steps[steps.length - 1].errorDetails
                                            ?
                                            (
                                                <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                                                    <strong>Details:</strong> {steps[steps.length - 1].errorDetails}
                                                </div>
                                            )
                                            :
                                            null
                                        }
                                    </div>
                                )
                                :
                                (
                                    <div
                                        style={{
                                            alignItems: 'center',
                                            backgroundColor: '#f9f9f9',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            minHeight: '100px',
                                            padding: 10,
                                            textAlign: 'center',
                                            width: '100%'
                                        }}
                                    >
                                        No workflow steps available.
                                    </div>
                                )
                            }
                            <div ref={stepsEndRef} />
                        </div>
                    </div>
                </Col>
                <Col span={1} />
            </Row>

            <Row gutter={16} style={{ padding: '16px', paddingBottom: '4px', paddingTop: '8px' }}>
                <Col span={1} />
                <Col span={22}>
                    <Button
                        // Prevent duplicate workflow starts while a request is already running.
                        disabled={ startButtonDisabled || isStartWorkflowLoading || isWorkflowRunning }
                        icon={<DeleteIcon />}
                        style={{width: '95%', backgroundColor: 'cornflowerblue'}}
                        type='primary'
                        onClick={handleStartButtonClick}
                    >
                        {startButtonText}
                    </Button>
                </Col>
                <Col span={1} />
            </Row>
        </Spin>
    );
};

export default MultiStepJobViewer;
